/**
 * Wall-clock <-> instant conversion for named IANA zones - pure, Intl only.
 *
 * The calendar feature works in two "time spaces":
 *  - instants: real UTC milliseconds (an event at 09:00 Europe/Stockholm),
 *  - wall clock: the written date and time stored as if it were UTC ("pseudo-UTC"), for floating
 *    times, all-day dates and recurrence expansion (rrule runs on wall-clock time, so a weekly
 *    Monday 00:30 meeting stays on Monday whatever the UTC date is).
 *
 * `wallToInstant` follows RFC 5545 §3.3.5: a wall time that occurs twice (DST overlap) is the
 * FIRST occurrence; a wall time that does not exist (DST gap) is read with the UTC offset in effect
 * before the gap (02:30 on a spring-forward night in Europe/Stockholm -> 03:30 CEST).
 */

import { timeZoneOffsetMs } from "@totodo/operations"

export const MINUTE_MS = 60_000
export const HOUR_MS = 3_600_000
export const DAY_MS = 86_400_000

/** Offsets change at most every 15 minutes (UTC); memoise per zone and quarter hour. */
const offsetCache = new Map<string, number>()
const OFFSET_CACHE_MAX = 100_000

/** `zone`'s offset from UTC at instant `ms`, in ms (east positive). */
export function zoneOffsetMs(ms: number, zone: string): number {
  if (zone === "UTC" || zone === "Etc/UTC") return 0
  const key = `${zone}|${Math.floor(ms / (15 * MINUTE_MS))}`
  let off = offsetCache.get(key)
  if (off === undefined) {
    off = timeZoneOffsetMs(ms, zone)
    if (offsetCache.size >= OFFSET_CACHE_MAX) offsetCache.clear()
    offsetCache.set(key, off)
  }
  return off
}

/** The wall clock of instant `ms` in `zone`, as pseudo-UTC ms. */
export function instantToWall(ms: number, zone: string): number {
  return ms + zoneOffsetMs(ms, zone)
}

/**
 * The instant at which the wall clock in `zone` reads `wallMs` (pseudo-UTC ms).
 * Overlap -> first occurrence; gap -> offset before the gap (RFC 5545 §3.3.5).
 */
export function wallToInstant(wallMs: number, zone: string): number {
  if (zone === "UTC" || zone === "Etc/UTC") return wallMs
  const before = zoneOffsetMs(wallMs - DAY_MS, zone)
  const after = zoneOffsetMs(wallMs + DAY_MS, zone)
  const tBefore = wallMs - before
  const tAfter = wallMs - after
  const beforeOk = zoneOffsetMs(tBefore, zone) === before
  const afterOk = zoneOffsetMs(tAfter, zone) === after
  if (beforeOk && afterOk) return Math.min(tBefore, tAfter)
  if (beforeOk) return tBefore
  if (afterOk) return tAfter
  // Neither offset gives this wall time: it is in a gap. Use the offset before the gap.
  return tBefore
}

/** Does the wall time `wallMs` exist in `zone` (false inside a DST gap)? */
export function wallTimeExists(wallMs: number, zone: string): boolean {
  return instantToWall(wallToInstant(wallMs, zone), zone) === wallMs
}

// ---------------------------------------------------------------------------
// Wall-clock fields
// ---------------------------------------------------------------------------

export interface WallFields {
  year: number
  /** 1-12 */
  month: number
  day: number
  hour: number
  minute: number
  second: number
}

export function wallFromFields(f: WallFields): number {
  return Date.UTC(f.year, f.month - 1, f.day, f.hour, f.minute, f.second)
}

const pad = (n: number, w = 2) => String(n).padStart(w, "0")

/** `YYYY-MM-DD` of a pseudo-UTC wall time. */
export function wallDayKey(wallMs: number): string {
  const d = new Date(wallMs)
  return `${pad(d.getUTCFullYear(), 4)}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`
}

/** Pseudo-UTC midnight of the wall day containing `wallMs`. */
export function wallStartOfDay(wallMs: number): number {
  return Math.floor(wallMs / DAY_MS) * DAY_MS
}

// ---------------------------------------------------------------------------
// The viewer's (runtime) zone
// ---------------------------------------------------------------------------

/** The runtime's own IANA zone (honours TZ in Node; the device zone in a browser). */
export function viewerZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"
  } catch {
    return "UTC"
  }
}

/** `YYYY-MM-DD` of instant `ms` on the runtime's local calendar. */
export function localDayKey(ms: number): string {
  const d = new Date(ms)
  return `${pad(d.getFullYear(), 4)}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/** Instant of local midnight starting the runtime-local day `YYYY-MM-DD` (+ `addDays`). */
export function localMidnight(dayKey: string, addDays = 0): number {
  const [y, m, d] = dayKey.split("-").map(Number) as [number, number, number]
  // The Date constructor moves a midnight that does not exist to the first instant of the day.
  return new Date(y, m - 1, d + addDays).getTime()
}

/** The runtime-local wall time `wallMs` (pseudo-UTC fields) as an instant. */
export function localWallToInstant(wallMs: number): number {
  const d = new Date(wallMs)
  return new Date(
    d.getUTCFullYear(),
    d.getUTCMonth(),
    d.getUTCDate(),
    d.getUTCHours(),
    d.getUTCMinutes(),
    d.getUTCSeconds(),
  ).getTime()
}
